import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  Alert,
  ActivityIndicator,
} from 'react-native';
import { ms, s, vs } from '../../../../utils/responsive';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { RootStackNavigationProp } from '../../../../types/navigation';
import { relationshipsService, RelationshipUser } from '../../../../services/relationships';
import { treatmentsService } from '../../../../services/treatments';
import { tasksService } from '../../../../services/tasks';

import { createLogger } from '../../../../utils/logger';
const logger = createLogger('TreatmentsUnlocked');

type RouteParams = {
  TreatmentsUnlocked: {
    treatmentId: string;
  };
};

type Contact = RelationshipUser;

export const TreatmentsUnlockedScreen: React.FC = () => {
  const navigation = useNavigation<RootStackNavigationProp>();
  const route = useRoute<RouteProp<Record<string, RouteParams['TreatmentsUnlocked']>, string>>();
  const treatmentId = (route.params as any)?.treatmentId as string | undefined;

  const [successors, setSuccessors] = useState<Contact[]>([]);
  const [keyholders, setKeyholders] = useState<Contact[]>([]);
  const [selectedContacts, setSelectedContacts] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadRelationshipsAndGrants();
  }, []);

  const loadRelationshipsAndGrants = async () => {
    try {
      setLoading(true);
      const data = await relationshipsService.getRelationships();
      setSuccessors(data.successors || []);
      setKeyholders(data.keyholders || []);

      if (treatmentId) {
        const grants = await treatmentsService.getTreatmentAccessGrants(treatmentId);
        const preselected = (grants || [])
          .filter((g: any) => g.canView && !g.isDeathEventOnly && g.status !== 'revoked')
          .map((g: any) => g.grantedToUserId || g.grantedToEmail)
          .filter(Boolean);
        setSelectedContacts(preselected);
      }
    } catch (error) {
      logger.error('Error loading relationships/grants:', error);
      Alert.alert('Error', 'Failed to load contacts. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const toggleContact = (contact: Contact) => {
    const identifier = contact.visibilityId || contact.id;
    setSelectedContacts(prev =>
      prev.includes(identifier)
        ? prev.filter(id => id !== identifier)
        : [...prev, identifier]
    );
  };

  const renderContact = (contact: Contact) => {
    const isSelected = selectedContacts.includes(contact.visibilityId || contact.id);
    const isPending = contact.status === 'pending';

    return (
      <TouchableOpacity
        key={contact.id}
        style={[styles.contactRow, isPending && styles.pendingContactRow]}
        onPress={() => toggleContact(contact)}
      >
        <View style={[styles.avatarPlaceholder, isPending && styles.pendingAvatar]}>
          <Text style={[styles.avatarText, isPending && styles.pendingAvatarText]}>
            {contact.name?.charAt(0)?.toUpperCase() || '?'}
          </Text>
        </View>
        <View style={styles.contactInfo}>
          <View style={styles.contactNameRow}>
            <Text style={styles.contactName}>{contact.name}</Text>
            {isPending && (
              <Text style={styles.pendingLabel}>Pending</Text>
            )}
          </View>
          <Text style={styles.contactEmail}>{contact.email}</Text>
        </View>
        <View style={[styles.checkbox, isSelected && styles.checkboxSelected]}>
          {isSelected && (
            <Image
              source={require('../../../../assets/icons/check.png')}
              style={styles.checkIcon}
            />
          )}
        </View>
      </TouchableOpacity>
    );
  };

  const handleSave = async () => {
    try {
      if (!treatmentId) {
        Alert.alert('Error', 'Missing treatment ID');
        return;
      }
      setSaving(true);
      const allContacts = [...successors, ...keyholders];
      const selectedEmails = allContacts
        .filter(c => selectedContacts.includes(c.visibilityId || c.id))
        .map(c => c.email)
        .filter(Boolean);

      await treatmentsService.unlockTreatment(treatmentId, selectedEmails);

      // CRITICAL: Mark task as completed
      await tasksService.completeTask('current_therapy');

      Alert.alert('Success', 'Access permissions saved successfully!', [
        { text: 'OK', onPress: () => (navigation as any).navigate('MedicalChecklist') },
      ]);
    } catch (error) {
      logger.error('Error saving treatment access:', error);
      Alert.alert('Error', 'Failed to save access permissions. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={[styles.container, { justifyContent: 'center', alignItems: 'center' }]}>
          <ActivityIndicator size="large" color="#1C2C4F" />
          <Text style={[styles.subtitle, { marginTop: 20 }]}>Loading contacts...</Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Image
            source={require('../../../../assets/icons/back.png')}
            style={styles.backIcon}
          />
        </TouchableOpacity>
        <Image
          source={require('../../../../assets/logo.png')}
          style={styles.logo}
          resizeMode="contain"
        />
      </View>

      <View style={styles.content}>
        <View style={styles.unlockIconContainer}>
          <Image
            source={require('../../../../assets/icons/BlueUnlock.png')}
            style={styles.unlockIcon}
            resizeMode="contain"
          />
          <Text style={styles.unlockText}>UNLOCKED</Text>
          <Text style={styles.subtitle}>Select who can view this data</Text>
        </View>

        <ScrollView style={styles.scrollView}>
          {successors.length > 0 && (
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>Successors</Text>
              {successors.map(renderContact)}
            </View>
          )}

          {keyholders.length > 0 && (
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>Key Holders</Text>
              {keyholders.map(renderContact)}
            </View>
          )}

          {successors.length === 0 && keyholders.length === 0 && (
            <View style={styles.emptyState}>
              <Text style={styles.emptyStateText}>No contacts found</Text>
              <Text style={styles.emptyStateSubtext}>Add successors or key holders first to share this information</Text>
              <TouchableOpacity
                style={styles.addSuccessorButton}
                onPress={() => (navigation as any).navigate('Profile', { screen: 'AddSuccessor' })}
              >
                <Text style={styles.addSuccessorButtonText}>+ Add Successor</Text>
              </TouchableOpacity>
            </View>
          )}
        </ScrollView>

        <TouchableOpacity
          style={styles.saveButton}
          onPress={handleSave}
        >
          {saving ? (
            <ActivityIndicator color="#FFFFFF" />
          ) : (
            <Text style={styles.saveButtonText}>Save Changes</Text>
          )}
        </TouchableOpacity>
      </View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F3F6F8',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: ms(15),
    paddingTop: ms(15),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    top: ms(15),
    padding: ms(5),
    zIndex: 1,
  },
  backIcon: {
    width: s(32),
    height: s(32),
    tintColor: '#333',
  },
  logo: {
    width: s(80),
    height: s(80),
  },
  content: {
    flex: 1,
    paddingHorizontal: ms(20),
  },
  unlockIconContainer: {
    alignItems: 'center',
    marginVertical: ms(20),
  },
  unlockIcon: {
    width: s(100),
    height: s(100),
  },
  unlockText: {
    fontSize: ms(24, 0.3),
    fontWeight: 'bold',
    color: '#1C2C4F',
    marginTop: ms(10),
  },
  subtitle: {
    fontSize: ms(18, 0.3),
    color: '#1C2C4F',
    marginTop: ms(5),
  },
  scrollView: {
    flex: 1,
    marginTop: ms(20),
  },
  section: {
    marginBottom: ms(30),
  },
  sectionTitle: {
    fontSize: ms(20, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
    marginBottom: ms(15),
  },
  contactRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: ms(10),
    marginBottom: ms(10),
  },
  avatarPlaceholder: {
    width: s(50),
    height: s(50),
    borderRadius: ms(25),
    backgroundColor: '#29BFC0',
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatarText: {
    fontSize: ms(18, 0.3),
    fontWeight: '600',
    color: '#FFFFFF',
  },
  contactInfo: {
    flex: 1,
    marginLeft: ms(15),
  },
  contactName: {
    fontSize: ms(16, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
  },
  contactEmail: {
    fontSize: ms(14, 0.3),
    color: '#666',
    marginTop: ms(2),
  },
  checkbox: {
    width: s(24),
    height: s(24),
    borderRadius: ms(12),
    borderWidth: ms(2),
    borderColor: '#29BFC0',
    marginLeft: ms(15),
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkboxSelected: {
    backgroundColor: '#F2994A',
    borderColor: '#F2994A',
  },
  checkIcon: {
    width: s(12),
    height: s(12),
    tintColor: '#FFFFFF',
  },
  emptyState: {
    alignItems: 'center',
    marginTop: ms(50),
    paddingHorizontal: ms(20),
  },
  emptyStateText: {
    fontSize: ms(18, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
    textAlign: 'center',
    marginBottom: ms(10),
  },
  emptyStateSubtext: {
    fontSize: ms(16, 0.3),
    color: '#666',
    textAlign: 'center',
    lineHeight: ms(24, 0.3),
  },
  pendingContactRow: {
    opacity: 0.8,
  },
  pendingAvatar: {
    backgroundColor: '#29BFC0',
  },
  pendingAvatarText: {
    color: '#FFFFFF',
  },
  contactNameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: ms(2),
  },
  pendingLabel: {
    fontSize: ms(12, 0.3),
    color: '#FFFFFF',
    fontWeight: '600',
    marginLeft: ms(8),
    paddingHorizontal: ms(8),
    paddingVertical: ms(2),
    backgroundColor: '#F2994A',
    borderRadius: ms(10),
  },
  saveButton: {
    backgroundColor: '#29BFC0',
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
    marginVertical: ms(20),
    marginBottom: ms(30),
  },
  saveButtonText: {
    color: '#FFF',
    fontSize: ms(18, 0.3),
    fontWeight: '600',
  },
  addSuccessorButton: {
    backgroundColor: '#29BFC0',
    paddingVertical: ms(12),
    paddingHorizontal: ms(24),
    borderRadius: ms(25),
    marginTop: ms(20),
  },
  addSuccessorButtonText: {
    color: '#FFFFFF',
    fontSize: ms(16, 0.3),
    fontWeight: '600',
  },
});
